/* SectionSourcesPanel — collapsible "Sources used" block shown under each
 * generated section in BlueprintViewPage. Fetches the curated source pool
 * for a country+section from /api/countries/{id}/sections/{section}/sources
 * and renders a numbered, categorized list with external-link icons.
 *
 * Lazy — only fetches when the user expands the panel. Cached for the
 * lifetime of the page.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, ChevronDown, ChevronRight, ExternalLink, Loader2, Shield, Lock, Unlock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact priority chip colors
const PRIO_STYLE = {
  Critical: 'text-red-700 bg-red-50 border-red-200',
  High:     'text-amber-700 bg-amber-50 border-amber-200',
  Medium:   'text-blue-700 bg-blue-50 border-blue-200',
  Low:      'text-slate-600 bg-slate-50 border-slate-200',
};

export default function SectionSourcesPanel({ countryId, sectionKey, darkMode = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchSources = useCallback(async () => {
    if (data || loading) return;
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/countries/${countryId}/sections/${sectionKey}/sources`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load sources');
    } finally { setLoading(false); }
  }, [countryId, sectionKey, data, loading]);

  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      if (next) fetchSources();
      return next;
    });
  };

  const tint = darkMode
    ? { bg: 'bg-white/5', border: 'border-white/10', hover: 'hover:bg-white/10', text: 'text-white', muted: 'text-white/50', chipBg: 'bg-white/5', chipBorder: 'border-white/10' }
    : { bg: 'bg-[#f8f9ff]', border: 'border-[#c3c6d7]/30', hover: 'hover:bg-[#eff4ff]', text: 'text-[#0b1c30]', muted: 'text-[#737686]', chipBg: 'bg-white', chipBorder: 'border-[#c3c6d7]/40' };

  return (
    <div className={`mt-4 rounded-xl border ${tint.border} ${tint.bg} overflow-hidden`} data-testid={`sources-panel-${sectionKey}`}>
      <button onClick={toggle} data-testid={`sources-toggle-${sectionKey}`}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left ${tint.hover} transition-all`}>
        <div className="flex items-center gap-2.5">
          <BookOpen className={`w-4 h-4 ${darkMode ? 'text-[#a78bfa]' : 'text-[#7c3aed]'}`} />
          <div>
            <p className={`text-xs font-bold uppercase tracking-widest ${tint.text}`}>Sources used</p>
            <p className={`text-[10.5px] ${tint.muted} leading-tight`}>
              {data ? `${data.total} curated institutional sources injected into this section's prompt` : 'Tap to inspect the 60 sources behind this section'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${darkMode ? 'bg-[#7c3aed]/20 text-[#a78bfa]' : 'bg-[#7c3aed]/10 text-[#7c3aed]'}`}>
              {data.total}
            </span>
          )}
          {open ? <ChevronDown className={`w-4 h-4 ${tint.muted}`} /> : <ChevronRight className={`w-4 h-4 ${tint.muted}`} />}
        </div>
      </button>

      {open && (
        <div className={`px-4 pb-4 pt-1 border-t ${tint.border}`} data-testid={`sources-body-${sectionKey}`}>
          {loading && (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className={`w-4 h-4 animate-spin ${tint.muted}`} />
              <span className={`text-xs ${tint.muted}`}>Loading curated source pool…</span>
            </div>
          )}
          {err && <p className="text-xs text-red-500 py-3">Error: {err}</p>}
          {data && (
            <>
              {/* Meta: categories queried + explainer */}
              <div className="flex flex-wrap items-center gap-1.5 mb-3 mt-2">
                <Shield className={`w-3 h-3 ${tint.muted}`} />
                <span className={`text-[10px] font-bold uppercase tracking-widest ${tint.muted}`}>Categories queried:</span>
                {data.categories_queried?.slice(0, 4).map(c => (
                  <span key={c} className={`text-[10px] px-1.5 py-0.5 rounded border ${tint.chipBorder} ${tint.chipBg} ${tint.text}`}>{c.split(',')[0]}</span>
                ))}
                {data.categories_queried?.length > 4 && <span className={`text-[10px] ${tint.muted}`}>+{data.categories_queried.length - 4} more</span>}
              </div>
              <p className={`text-[11px] ${tint.muted} mb-3 leading-relaxed`}>
                Claude is explicitly instructed to cite only sources from this pool by name + year (e.g. "(World Bank WDI, 2024)"),
                not to invent URLs, and not to reference sources outside this list.
              </p>
              {/* Grid of sources */}
              <div className="grid md:grid-cols-2 gap-1.5">
                {data.sources.map((s, idx) => {
                  const paywallFree = (s.paywall_status || '').toLowerCase() === 'free';
                  const PayIcon = paywallFree ? Unlock : Lock;
                  const prioCls = darkMode
                    ? 'text-white/70 bg-white/10 border-white/10'
                    : (PRIO_STYLE[s.priority] || PRIO_STYLE.Low);
                  return (
                    <div key={s.source_id || idx} className={`flex items-start gap-2 p-2.5 rounded-lg ${tint.chipBg} border ${tint.chipBorder}`} data-testid={`source-${s.source_id}`}>
                      <span className={`text-[10px] font-black ${tint.muted} w-5 shrink-0 mt-0.5`}>{String(idx + 1).padStart(2, '0')}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[12px] font-bold truncate ${tint.text}`}>{s.name}</p>
                        {s.url && (
                          <a href={s.url.startsWith('http') ? s.url : `https://${s.url}`} target="_blank" rel="noopener noreferrer"
                            className={`text-[10px] ${darkMode ? 'text-[#a78bfa] hover:text-[#c4b5fd]' : 'text-[#7c3aed] hover:text-[#5b21b6]'} flex items-center gap-1 truncate`}>
                            <span className="truncate">{s.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                            <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                          </a>
                        )}
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {s.priority && <span className={`text-[9px] font-bold uppercase tracking-wider px-1 py-0 rounded border ${prioCls}`}>{s.priority}</span>}
                          <span className={`text-[9px] flex items-center gap-0.5 ${tint.muted}`}><PayIcon className="w-2 h-2" />{paywallFree ? 'Free' : (s.paywall_status || 'Gated')}</span>
                          {s.api_access?.toLowerCase() === 'yes' && <span className={`text-[9px] ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>· API</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
